import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Box,
  Stack,
  Card,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  Divider,
} from '@mui/material';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import DevicesOtherRoundedIcon from '@mui/icons-material/DevicesOtherRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AddToQueueRoundedIcon from '@mui/icons-material/AddToQueueRounded';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { Pathname } from 'Routes';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

// ── Mock "signed-in devices". There's no list-devices API yet, so this stands
// in until one exists. Swap fetchDevices()/signOutDevice() for real calls when
// the endpoints land; the UI stays the same.
const MOCK_DEVICES = [
  {
    id: 'dev-web', name: 'This browser', type: 'web', platform: 'Chrome · macOS', location: 'Chennai, IN', lastActive: 'Active now', current: true,
  },
  {
    id: 'dev-tv1', name: 'Living Room TV', type: 'tv', platform: 'Samsung Tizen', location: 'Chennai, IN', lastActive: 'Active now', current: false,
  },
  {
    id: 'dev-mob', name: 'iPhone 15', type: 'mobile', platform: 'iOS 18', location: 'Chennai, IN', lastActive: '2 hours ago', current: false,
  },
  {
    id: 'dev-tv2', name: 'Bedroom Fire TV', type: 'tv', platform: 'Fire OS', location: 'Bengaluru, IN', lastActive: 'Yesterday', current: false,
  },
];

const LINK_STEPS = [
  'Open Projector on your TV and choose “Sign in”.',
  'A short login code appears on the screen.',
  'Type that code above and select “Link device”.',
];

const DEVICE_ICON = {
  tv: TvRoundedIcon,
  mobile: SmartphoneRoundedIcon,
  web: ComputerRoundedIcon,
};
const deviceIcon = (type) => DEVICE_ICON[type] || DevicesOtherRoundedIcon;

const DeviceCard = ({ device, onSignOut, busy }) => {
  const Icon = deviceIcon(device.type);
  return (
    <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box
          sx={{
            width: 42, height: 42, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary',
          }}
        >
          <Icon />
        </Box>
        {device.current ? (
          <Chip size="small" color="primary" label="This device" sx={{ height: 22, fontWeight: 700 }} />
        ) : (
          <Tooltip title="Sign out of this device">
            <span>
              <IconButton size="small" color="error" onClick={() => onSignOut(device)} disabled={busy}>
                {busy ? <CircularProgress size={16} color="inherit" /> : <LogoutRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }} noWrap>{device.name}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{device.platform}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 'auto' }}>
        {[device.location, device.lastActive].filter(Boolean).join('  ·  ')}
      </Typography>
    </Card>
  );
};

export const ManageDeviceV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoCode = searchParams.get('code');
  const isAutoMode = Boolean(autoCode);

  const { langText, setAppSnackbar } = useContext(AppContext);
  const manageDeviceText = langText?.manageDevice;

  const [code, setCode] = useState(autoCode || '');
  const [devices, setDevices] = useState(null); // null = loading
  const [signingOutId, setSigningOutId] = useState(null);
  const redirectedRef = useRef(false);

  // A TV code in the URL (?code=…) is NOT acted on here any more. It used to
  // link the device on mount, which meant opening the link was enough to
  // attach a TV to the account. Hand off to the consent screen instead, which
  // asks the user to Allow or Decline first.
  useEffect(() => {
    if (!isAutoMode || redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(`${Pathname.tvLoginApprove}?code=${encodeURIComponent(autoCode)}`, { replace: true });
  }, [isAutoMode, autoCode]);

  // Mock device fetch — replace with a real list-devices call when available.
  useEffect(() => {
    if (isAutoMode) return undefined;
    let mounted = true;
    const t = setTimeout(() => { if (mounted) setDevices(MOCK_DEVICES); }, 600);
    return () => { mounted = false; clearTimeout(t); };
  }, [isAutoMode]);

  const codeIsValid = code.trim().length >= 6;

  const { isValidating: linking, mutate: linkDevice } = useSWR([endpoints.registerTv, code], {
    fetcher: (url, inputCode) => dataFetcher(url, { code: inputCode }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setAppSnackbar({ isVisible: true, type: 'success', message: 'Device linked successfully' });
        // Optimistically add the freshly-linked device (mock).
        setDevices((prev) => ([
          {
            id: `linked-${code}`, name: 'New TV', type: 'tv', platform: 'Linked via code', location: '—', lastActive: 'Active now', current: false,
          },
          ...(prev || []),
        ]));
        setCode('');
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: message || 'That code didn’t work. Try again.' });
      }
    },
    onError: () => {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Something went wrong linking the device.' });
    },
  });

  const signOutDevice = (device) => {
    // Mock sign-out — replace with a real revoke call when available.
    setSigningOutId(device.id);
    setTimeout(() => {
      setDevices((prev) => (prev || []).filter((d) => d.id !== device.id));
      setSigningOutId(null);
      setAppSnackbar({ isVisible: true, type: 'success', message: `Signed out of ${device.name}` });
    }, 500);
  };

  const deviceCount = devices?.length || 0;

  // Redirecting to the consent screen — render nothing rather than flashing
  // the device manager for a frame.
  if (isAutoMode) return null;

  return (
    <V2SettingsShell
      title="TV & devices"
      description="Link a TV with a login code and manage the devices signed in to your account."
      // No bottom tab bar on this screen — you get here from Settings and leave
      // through "Back to Settings".
      hideDock
    >
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.7fr' }, gap: 2, alignItems: 'stretch',
      }}
      >
        {/* LEFT: enter login code — full-height card */}
        <Card sx={{ p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 0.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'primary.contrastText',
            }}
            >
              <AddToQueueRoundedIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Enter login code</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            On your TV, open Projector and enter the code shown there to link it to your account.
          </Typography>
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
            label="TV code"
            placeholder="e.g. 4X9K2P"
            fullWidth
            inputProps={{ style: { letterSpacing: 3, fontWeight: 700, textTransform: 'uppercase' } }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={!codeIsValid || linking}
            onClick={() => linkDevice()}
            startIcon={linking ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {linking ? (`${manageDeviceText?.addingDevice || 'Linking'}…`) : (manageDeviceText?.addDevice || 'Link device')}
          </Button>

          {/* How-to steps fill the rest of the full-height card. */}
          <Divider sx={{ my: 3 }} />
          <Typography sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 1.5,
          }}
          >
            How to link a TV
          </Typography>
          <Stack spacing={1.75}>
            {LINK_STEPS.map((step, i) => (
              <Stack key={step} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 22, height: 22, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.primary', fontSize: 12, fontWeight: 700,
                }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>{step}</Typography>
              </Stack>
            ))}
          </Stack>
        </Card>

        {/* RIGHT: signed-in devices — smaller, aligned cards */}
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Signed-in devices</Typography>
            {devices !== null && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {`${deviceCount} ${deviceCount === 1 ? 'device' : 'devices'}`}
              </Typography>
            )}
          </Stack>
          {devices === null ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignContent: 'start' }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={122} animation="wave" />
              ))}
            </Box>
          ) : deviceCount === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No devices are signed in right now.</Typography>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignContent: 'start' }}>
              {devices.map((d) => (
                <DeviceCard key={d.id} device={d} onSignOut={signOutDevice} busy={signingOutId === d.id} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </V2SettingsShell>
  );
};

export default ManageDeviceV2;
